import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { RentalPriceChangeCard } from './ui/rental_price_change.js';

export { RentalPriceChangeCard } from './ui/rental_price_change.js';

type RentalPriceChangeUi = Extract<ChatUi, { kind: 'rental_price_change' }>;

/**
 * "Continuar mesmo assim" dispara um `rental-select` estruturado quando há
 * `offerId` (mesmo canal do botão "Alugar": revalida pelo id + o novo preço
 * vira o baseline); senão cai no `interaction` (com `challengeId`) ou `reply`
 * em texto livre (cards antigos). "Cancelar" volta para a busca via `reply`.
 *
 * **Componente e não um lambda no `render` (#2327, mesmo padrão de
 * `bus/bus_offers/index.tsx`).** O eco precisa do locale da sessão, e `render`
 * é uma função de catálogo onde hook não roda.
 */
function RentalPriceChangeEntry({
  ui,
  note,
  readOnly,
  dispatch,
}: Readonly<{
  ui: RentalPriceChangeUi;
  note?: string;
  readOnly?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t } = useGenUiI18n();
  return (
    <RentalPriceChangeCard
      priceChange={ui.priceChange}
      note={note}
      readOnly={readOnly}
      onContinue={
        readOnly
          ? undefined
          : () => {
              const { offerId, newPrice, challengeId } = ui.priceChange;
              if (offerId) {
                dispatch({
                  type: 'rental-select',
                  offerId,
                  // Novo preço já visto e aceito — vira o baseline da revalidação.
                  price: newPrice,
                  text: t('rentalPriceChange.replyContinue'),
                  analytics: { actionId: 'continue' },
                });
              } else if (challengeId) {
                dispatch({
                  type: 'interaction',
                  interactionId: challengeId,
                  text: t('rentalPriceChange.replyContinueChallenge'),
                  analytics: { actionId: 'continue' },
                });
              } else {
                dispatch({
                  type: 'reply',
                  text: t('rentalPriceChange.replyContinue'),
                  analytics: { actionId: 'continue' },
                });
              }
            }
      }
      onCancel={
        readOnly
          ? undefined
          : () => {
              dispatch({
                type: 'reply',
                text: t('rentalPriceChange.replyCancel'),
                analytics: { actionId: 'cancel' },
              });
            }
      }
    />
  );
}

/** Entrada de catálogo do `rental_price_change` (slot chat). */
export const rentalPriceChangeEntry: GenUiEntry<RentalPriceChangeUi> = {
  kind: 'rental_price_change',
  slot: 'chat',
  policy: { productOf: () => 'rental' },
  render: ({ ui, note, readOnly, dispatch }) => (
    <RentalPriceChangeEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      dispatch={dispatch}
    />
  ),
};
